import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export default function AgencyClients({ user }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load clients & tasks
  const loadClientsData = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/client-module/clients').catch(() => []),
      api.get('/api/agency-crm/tasks').catch(() => []),
      api.get('/api/staff-module/tasks').catch(() => []),
    ])
      .then(([clientsRes, tasksRes, legacyTasksRes]) => {
        setClients(clientsRes || []);
        const combined = [...(tasksRes || []), ...(legacyTasksRes || [])];
        setTasks(combined);
        if (selectedClient) {
          const updated = clientsRes.find(c => c.id === selectedClient.id);
          if (updated) setSelectedClient(updated);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadClientsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && clients.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading CRM Clients Directory…</div>;
  }
  if (selectedClient) {
    const clientTasks = tasks.filter(t => 
      String(t.clientName || '').trim().toLowerCase() === String(selectedClient.clientName || '').trim().toLowerCase()
    );
    const completedTasks = clientTasks.filter(t => t.status === 'Completed');
    const remainingTasks = clientTasks.filter(t => t.status !== 'Completed');
    const progressPct = clientTasks.length > 0 ? Math.round((completedTasks.length / clientTasks.length) * 100) : 0;

    // Combine timeline events from all service orders sorted desc
    const timeline = [];
    (selectedClient.serviceOrders || []).forEach(order => {
      const orderTimeline = order.timeline || [];
      orderTimeline.forEach(evt => {
        timeline.push({
          ...evt,
          serviceName: order.serviceName
        });
      });
    });
    timeline.sort((a, b) => String(b.date).localeCompare(String(a.date)));

    return (
      <div style={{ fontFamily: 'system-ui, sans-serif' }}>
        <button onClick={() => setSelectedClient(null)} style={{ background: '#f1f5f9', border: 'none', padding: '10px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 20 }}>
          ← Back to Directory
        </button>

        {/* Header Glass Card */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff', borderRadius: 20, padding: 32, marginBottom: 30, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase' }}>CRM Profile View</div>
            <h2 style={{ margin: '6px 0 2px', fontSize: '2rem', fontWeight: 900 }}>{selectedClient.clientName}</h2>
            <p style={{ margin: 0, opacity: 0.8, fontSize: '0.92rem' }}>{selectedClient.company || '—'} · {selectedClient.mobile || '—'}</p>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.08)', padding: '16px 26px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.15)', textAlign: 'center', minWidth: 150 }}>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginBottom: 6 }}>Overall Work Progress</div>
            <div style={{ fontSize: '2rem', fontWeight: 900, color: '#34d399' }}>{progressPct}%</div>
            <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: 4 }}>{completedTasks.length} / {clientTasks.length} Done</div>
          </div>
        </div>

        {/* 2-Column Details Dashboard */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 26 }}>
          {/* Left Column: Work Lists */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Pending Tasks */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800, color: '#d97706' }}>Remaining Tasks ({remainingTasks.length})</h3>
              {remainingTasks.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>All deliverables completed!</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {remainingTasks.map(t => (
                    <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fef3c7', borderRadius: 12, border: '1px solid #fde68a' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{t.serviceName}</span>
                      <span style={{ color: '#64748b', fontWeight: 800, background: '#fff', padding: '3px 8px', borderRadius: 20, fontSize: '0.72rem' }}>Due: {t.dueDate}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Completed Work */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800, color: '#16a34a' }}>Completed Work ({completedTasks.length})</h3>
              {completedTasks.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No tasks completed yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {completedTasks.map(t => (
                    <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#16a34a' }}>{t.serviceName}</span>
                      <span style={{ color: '#16a34a', fontWeight: 800, background: '#fff', padding: '3px 8px', borderRadius: 20, fontSize: '0.72rem' }}>Done</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Timeline & Meta */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Timeline */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800 }}>Client Timeline</h3>
              {timeline.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No timeline events recorded yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {timeline.map((evt, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 14 }}>
                      <span style={{ fontSize: '1.2rem' }}>🕒</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{evt.event}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>{evt.date} · {evt.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* General Settings */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 26, border: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 800 }}>Package & Contract</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '0.88rem' }}>
                <div><strong>Package / Services:</strong> {selectedClient.selectedServicesStr || (selectedClient.serviceOrders || []).map(o => `${o.serviceName} (${o.quantity} ${o.unit})`).join(', ') || 'No active services'}</div>
                <div><strong>Start Date:</strong> {selectedClient.startDate || selectedClient.joinDate || '—'}</div>
                <div><strong>End Date:</strong> {selectedClient.endDate || '—'}</div>
                <div><strong>Renewal Date:</strong> {selectedClient.renewalDate || '—'}</div>
                <div><strong>Status:</strong> <span style={{ padding: '3px 8px', borderRadius: 20, background: selectedClient.status === 'Active' ? '#dcfce7' : '#fee2e2', color: selectedClient.status === 'Active' ? '#16a34a' : '#dc2626', fontWeight: 800, fontSize: '0.75rem' }}>{selectedClient.status}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Directory Grid View
  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontWeight: 900, fontSize: '1.4rem' }}>CRM Clients Directory</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
        {clients.map(c => (
          <div
            key={c.id}
            onClick={() => setSelectedClient(c)}
            style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22,
              boxShadow: '0 4px 12px rgba(0,0,0,0.02)', cursor: 'pointer', transition: 'all 0.2s ease'
            }}
          >
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>{c.clientName}</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>{c.company || '—'}</div>
            <div style={{ fontSize: '0.78rem', color: '#224eae', fontWeight: 700, marginTop: 6 }}>
              📦 Services: {(c.serviceOrders || []).map(o => o.serviceName).join(', ') || 'No active service'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, background: c.status === 'Active' ? '#dcfce7' : '#fee2e2', color: c.status === 'Active' ? '#16a34a' : '#dc2626', padding: '3px 8px', borderRadius: 20 }}>{c.status}</span>
              <span style={{ fontSize: '0.75rem', color: '#224eae', fontWeight: 700 }}>View Profile →</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
